// Data Processing Module - Interval and time series processing
// May contain database queries for data retrieval, but no caching

export interface OptimalIntervals {
  count: number;
  durationMs: number;
  labels: string[];
}

// Pure data processing functions extracted from telemetryService
export async function processIntervalData(
  sessionId: string,
  intervals: any[],
  telemetryData: any[]
): Promise<any[]> {
  if (!telemetryData || telemetryData.length === 0) {
    return [];
  }

  // Map stroke metrics to intervals and calculate averages
  const chartData: any[] = [];

  intervals.forEach((interval: any, index: number) => {
    // Find telemetry data within this interval's time range
    const intervalTelemetryData = telemetryData.filter(
      (data: any) => data.timestamp >= interval.start_time && data.timestamp <= interval.end_time
    );

    // Calculate average power for each rower in this interval
    const rowerPowers: { [key: string]: number[] } = {};

    intervalTelemetryData.forEach((data: any) => {
      const rowerPosition = data.rower_position;
      const gateForce = data.gate_force_x;

      // Calculate power using physics: Power = Force × Velocity
      const gateVelocity = data.gate_angle_velocity;
      if (
        gateForce !== null &&
        gateForce !== undefined &&
        gateVelocity !== null &&
        gateVelocity !== undefined &&
        rowerPosition
      ) {
        const power = Math.abs(gateForce) * Math.abs(gateVelocity);
        const rowerKey = `Rower ${rowerPosition}`;

        if (!rowerPowers[rowerKey]) {
          rowerPowers[rowerKey] = [];
        }
        rowerPowers[rowerKey].push(power);
      }
    });

    // Create interval label based on duration
    const durationSeconds = Math.round(interval.duration_ms / 1000);
    const startMinutes = Math.floor((index * durationSeconds) / 60);
    const startSeconds = (index * durationSeconds) % 60;
    const endMinutes = Math.floor(((index + 1) * durationSeconds) / 60);
    const endSeconds = ((index + 1) * durationSeconds) % 60;

    let intervalLabel;
    if (intervals.length === 1) {
      if (endMinutes > 0) {
        intervalLabel = `Interval 1 (0-${endMinutes}min${endSeconds > 0 ? `${endSeconds}s` : ''})`;
      } else {
        intervalLabel = `Interval 1 (0-${endSeconds}s)`;
      }
    } else {
      const startLabel =
        startMinutes > 0
          ? `${startMinutes}min${startSeconds > 0 ? `${startSeconds}s` : ''}`
          : `${startSeconds}s`;
      const endLabel =
        endMinutes > 0
          ? `${endMinutes}min${endSeconds > 0 ? `${endSeconds}s` : ''}`
          : `${endSeconds}s`;
      intervalLabel = `Interval ${interval.interval_number} (${startLabel === '0s' ? '0' : startLabel}-${endLabel})`;
    }

    // Build row data in telemetry interval format
    const row: any = { interval: intervalLabel };

    // Dynamically detect active rower positions from telemetry data
    const activeRowerPositions = new Set<number>();
    intervalTelemetryData.forEach((data: any) => {
      if (data.rower_position && data.gate_force_x !== null && data.gate_force_x !== undefined) {
        activeRowerPositions.add(data.rower_position);
      }
    });

    // If no data for this interval, detect from all telemetry data in session
    if (activeRowerPositions.size === 0 && telemetryData.length > 0) {
      telemetryData.forEach((data: any) => {
        if (data.rower_position && data.gate_force_x !== null && data.gate_force_x !== undefined) {
          activeRowerPositions.add(data.rower_position);
        }
      });
    }

    // Add data for active rowers only
    Array.from(activeRowerPositions)
      .sort()
      .forEach((rower) => {
        const rowerKey = `Rower ${rower}`;
        if (rowerPowers[rowerKey] && rowerPowers[rowerKey].length > 0) {
          // Average the power values for this rower in this interval
          const avg =
            rowerPowers[rowerKey].reduce((a, b) => a + b, 0) / rowerPowers[rowerKey].length;
          row[rowerKey] = Math.round(avg);
        } else {
          row[rowerKey] = 0;
        }
      });

    chartData.push(row);
  });

  return chartData;
}

export async function generateTimeSeries(
  _sessionId: string,
  _telemetryData: any[]
): Promise<Array<{ time: number; [rower: string]: number }>> {
  // This function would be used for time series generation
  // For now, return empty array as this is complex logic that would need more context
  return [];
}

export function createOptimalIntervals(durationMs: number): OptimalIntervals {
  // Handle edge cases
  if (durationMs <= 0) {
    return {
      count: 1,
      durationMs: 0,
      labels: ['Full Session'],
    };
  }

  const durationMinutes = durationMs / (60 * 1000);
  let intervalCount: number;

  // Determine optimal interval count based on duration
  if (durationMinutes <= 1) {
    intervalCount = 1;
  } else if (durationMinutes <= 3) {
    intervalCount = 2;
  } else if (durationMinutes <= 8) {
    intervalCount = 3;
  } else {
    intervalCount = 4;
  }

  const intervalDurationMs = Math.floor(durationMs / intervalCount);
  const labels: string[] = [];

  for (let i = 0; i < intervalCount; i++) {
    const startMs = i * intervalDurationMs;
    const endMs = (i + 1) * intervalDurationMs;

    if (intervalCount === 1) {
      labels.push(formatDuration(durationMs));
    } else {
      // Special formatting for interval labels
      const startLabel = i === 0 && durationMinutes >= 2 ? '0 min' : formatDuration(startMs);
      const endLabel = formatDuration(Math.min(endMs, durationMs));
      labels.push(`${startLabel}-${endLabel}`);
    }
  }

  return {
    count: intervalCount,
    durationMs: intervalDurationMs,
    labels,
  };
}

// Helper function to format duration in a human-readable way
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // Special case for 0
  if (totalSeconds === 0) {
    return '0 sec';
  }

  if (minutes === 0) {
    return `${seconds} sec`;
  }

  // For clean minute values, show as "X min"
  if (seconds === 0) {
    return `${minutes} min`;
  }

  // Otherwise show as M:SS
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
